"use client"

import { useState } from "react"
import { Avatar, AvatarFallback } from "@/components/ui/avatar"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs"

const allActivities = [
  {
    id: 1,
    user: {
      initials: "JD",
      name: "John Doe",
    },
    action: "Uploaded 5 images to Magic Background",
    feature: "Magic Background",
    timestamp: "2 minutes ago",
    status: "success",
  },
  {
    id: 2,
    user: {
      initials: "SS",
      name: "Sarah Smith",
    },
    action: "Created a new room visualization",
    feature: "Room Visualizer",
    timestamp: "15 minutes ago",
    status: "success",
  },
  {
    id: 3,
    user: {
      initials: "MJ",
      name: "Mike Johnson",
    },
    action: "Performed a Youzu Lens search",
    feature: "Youzu Lens",
    timestamp: "32 minutes ago",
    status: "success",
  },
  {
    id: 4,
    user: {
      initials: "ED",
      name: "Emma Davis",
    },
    action: "Converted 3 images to 3D models",
    feature: "2D to 3D",
    timestamp: "1 hour ago",
    status: "warning",
  },
  {
    id: 5,
    user: {
      initials: "AW",
      name: "Alex Wilson",
    },
    action: "API integration failed",
    feature: "API",
    timestamp: "2 hours ago",
    status: "error",
  },
  {
    id: 6,
    user: {
      initials: "TK",
      name: "Tom King",
    },
    action: "Updated product catalog",
    feature: "Youzu Lens",
    timestamp: "3 hours ago",
    status: "success",
  },
  {
    id: 7,
    user: {
      initials: "LM",
      name: "Lisa Miller",
    },
    action: "Generated 10 background variations",
    feature: "Magic Background",
    timestamp: "4 hours ago",
    status: "success",
  },
  {
    id: 8,
    user: {
      initials: "RB",
      name: "Ryan Brown",
    },
    action: "Exported 3D model to GLTF format",
    feature: "2D to 3D",
    timestamp: "5 hours ago",
    status: "success",
  },
  {
    id: 9,
    user: {
      initials: "JW",
      name: "Jessica White",
    },
    action: "Created new room template",
    feature: "Room Visualizer",
    timestamp: "6 hours ago",
    status: "success",
  },
  {
    id: 10,
    user: {
      initials: "DM",
      name: "David Miller",
    },
    action: "Batch processing failed",
    feature: "Magic Background",
    timestamp: "7 hours ago",
    status: "error",
  },
  {
    id: 11,
    user: {
      initials: "KL",
      name: "Karen Lee",
    },
    action: "Updated API credentials",
    feature: "API",
    timestamp: "8 hours ago",
    status: "success",
  },
  {
    id: 12,
    user: {
      initials: "PJ",
      name: "Paul Jones",
    },
    action: "Performed bulk image search",
    feature: "Youzu Lens",
    timestamp: "9 hours ago",
    status: "success",
  },
]

export function ActivitiesList() {
  const [filter, setFilter] = useState("all")
  const [statusFilter, setStatusFilter] = useState("all")

  const filteredActivities = allActivities.filter((activity) => {
    if (filter !== "all" && activity.feature.toLowerCase() !== filter) {
      return false
    }
    if (statusFilter !== "all" && activity.status !== statusFilter) {
      return false
    }
    return true
  })

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <Tabs defaultValue="all" onValueChange={setFilter}>
          <TabsList>
            <TabsTrigger value="all">All</TabsTrigger>
            <TabsTrigger value="youzu lens">Youzu Lens</TabsTrigger>
            <TabsTrigger value="room visualizer">Room Visualizer</TabsTrigger>
            <TabsTrigger value="magic background">Magic Background</TabsTrigger>
            <TabsTrigger value="2d to 3d">2D to 3D</TabsTrigger>
          </TabsList>
        </Tabs>
        <div className="flex items-center gap-2">
          <Select defaultValue="all" onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="success">Success</SelectItem>
              <SelectItem value="warning">Warning</SelectItem>
              <SelectItem value="error">Error</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <Card>
        <CardContent className="p-6">
          <div className="space-y-5">
            {filteredActivities.length > 0 ? (
              filteredActivities.map((activity) => (
                <div key={activity.id} className="flex items-start gap-4">
                  <Avatar className="h-10 w-10 border-2 border-background">
                    <AvatarFallback className="bg-gradient-to-br from-pink-500 to-purple-500 text-white">
                      {activity.user.initials}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 space-y-1">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                      <p className="text-sm font-medium">{activity.action}</p>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-xs">
                          {activity.feature}
                        </Badge>
                        <Badge
                          variant={
                            activity.status === "success"
                              ? "default"
                              : activity.status === "warning"
                                ? "outline"
                                : "destructive"
                          }
                          className={
                            activity.status === "success"
                              ? "bg-green-500 hover:bg-green-600"
                              : activity.status === "warning"
                                ? "border-amber-500 text-amber-500"
                                : ""
                          }
                        >
                          {activity.status === "success"
                            ? "Success"
                            : activity.status === "warning"
                              ? "Warning"
                              : "Error"}
                        </Badge>
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <p className="text-xs text-muted-foreground">{activity.user.name}</p>
                      <p className="text-xs text-muted-foreground">{activity.timestamp}</p>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="flex h-40 items-center justify-center">
                <p className="text-muted-foreground">No activities found</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-center">
        <Button variant="outline">Load More</Button>
      </div>
    </div>
  )
}
